import axios from "axios";
import { hideModal } from "../data/modal";

/*****************/
/* INITIAL STATE */
/*****************/
const initialState = {
  loading: false,
  error: "",
  data: [],
};

/*********/
/* TYPES */
/*********/
const FETCH_CONTAINER_UPLOADS = "FETCH_CONTAINER_UPLOADS";
const FETCH_CONTAINER_UPLOADS_SUCCESS = "FETCH_CONTAINER_UPLOADS_SUCCESS";
const FETCH_CONTAINER_UPLOADS_ERROR = "FETCH_CONTAINER_UPLOADS_ERROR";
const PUT_CONTAINER_UPLOAD = "PUT_CONTAINER_UPLOAD";
const POST_CONTAINER_UPLOAD = "POST_CONTAINER_UPLOAD";
const FETCH_NEXT_CONTAINER_UPLOADS = "FETCH_NEXT_CONTAINER_UPLOADS";
const FETCH_NEXT_CONTAINER_UPLOADS_SUCCESS =
  "FETCH_NEXT_CONTAINER_UPLOADS_SUCCESS";
const DELETE_CONTAINER_UPLOAD = "DELETE_CONTAINER_UPLOAD";

/*******************/
/* HELPERS */
/*******************/
const getApiUrl = () => {
  const rawApiUrl = process.env.REACT_APP_API_URL || "";
  return rawApiUrl.replace(/\/+$/g, "");
};

const getAppUrl = () => {
  const rawAppUrl =
    process.env.REACT_APP_URL || process.env.REACT_APP_API_URL || "";
  return rawAppUrl.replace(/\/+$/g, "");
};

const getUploadApiId = (upload) => {
  if (!upload) {
    return "";
  }

  if (upload["@id"]) {
    return upload["@id"];
  }

  if (upload.id !== null && upload.id !== undefined) {
    return `/api/container_uploads/${upload.id}`;
  }

  return "";
};

/*******************/
/* ACTION CREATORS */
/*******************/
export const getContainerUploads = () => async (dispatch) => {
  dispatch(loadContainerUploads());

  try {
    const response = await axios({
      method: "get",
      url: `${getApiUrl()}/container_uploads?page=1`,
      headers: {
        "Content-Type": "application/ld+json; charset=utf-8",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    const members = Array.isArray(response.data?.["hydra:member"])
      ? response.data["hydra:member"]
      : [];

    dispatch(setContainerUploads(members));

    const nextUrl = response.data?.["hydra:view"]?.["hydra:next"];

    if (nextUrl) {
      dispatch(getNextContainerUploads(nextUrl));
    }

    if (members.length === 0) {
      dispatch(setError(""));
    }
  } catch (error) {
    console.error("Fetch container uploads failed", error);
    dispatch(setContainerUploads([]));
    dispatch(setError("Failed to load container uploads"));
  }
};

export const getNextContainerUploads = (nextUrl) => async (dispatch) => {
  dispatch(loadNextContainerUploads());

  try {
    const response = await axios({
      method: "get",
      url: `${getAppUrl()}${nextUrl}`,
      headers: {
        "Content-Type": "application/ld+json; charset=utf-8",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    const members = Array.isArray(response.data?.["hydra:member"])
      ? response.data["hydra:member"]
      : [];

    dispatch(setNextContainerUploads(members));

    const followingNextUrl = response.data?.["hydra:view"]?.["hydra:next"];

    if (followingNextUrl) {
      dispatch(getNextContainerUploads(followingNextUrl));
    }
  } catch (error) {
    console.error("Fetch next container uploads failed", error);
    dispatch(setError("Failed to load next container uploads"));
  }
};

export const updateContainerUpload =
  (containerUpload) => async (dispatch, getState) => {
    dispatch(putContainerUpload());

    const {
      containerUploads: { data },
    } = getState();

    try {
      const response = await axios({
        method: "put",
        url: `${getAppUrl()}${containerUpload["@id"]}`,
        headers: {
          "Content-Type": "application/ld+json; charset=utf-8",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        data: containerUpload,
      });

      if (response.status === 200) {
        const resultData = data.map((item) => {
          if (item["@id"] === response.data["@id"]) {
            return response.data;
          }

          return item;
        });

        dispatch(setContainerUploads(resultData));
        dispatch(hideModal());
      } else {
        dispatch(setError("Something went wrong updating"));
      }
    } catch (error) {
      console.error("Update container upload failed", error);
      dispatch(setError("Something went wrong updating"));
    }
  };

export const createContainerUpload =
  (containerUpload) => async (dispatch, getState) => {
    dispatch(postContainerUpload());

    const {
      containerUploads: { data },
    } = getState();

    try {
      const response = await axios({
        method: "post",
        url: `${getApiUrl()}/container_uploads`,
        headers: {
          "Content-Type": "application/ld+json; charset=utf-8",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        data: containerUpload,
      });

      if (response.status === 201) {
        const resultData = [...data, response.data];

        dispatch(setContainerUploads(resultData));
        dispatch(hideModal());
      } else {
        dispatch(setError("Something went wrong creating"));
      }
    } catch (error) {
      console.error("Create container upload failed", error);
      dispatch(setError("Something went wrong creating"));
    }
  };

export const deleteContainerUpload = (upload) => async (dispatch, getState) => {
  dispatch({ type: DELETE_CONTAINER_UPLOAD });

  try {
    const uploadApiId = getUploadApiId(upload);

    if (!uploadApiId) {
      throw new Error("Missing container upload id");
    }

    const response = await axios({
      method: "delete",
      url: `${getAppUrl()}${uploadApiId}`,
      headers: {
        "Content-Type": "application/ld+json; charset=utf-8",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    if (response.status === 204) {
      const { data } = getState().containerUploads;

      const filtered = data.filter((item) => item["@id"] !== uploadApiId);

      dispatch(setContainerUploads(filtered));
      dispatch(hideModal());

      await dispatch(getContainerUploads());
    } else {
      throw new Error("Failed to delete upload");
    }
  } catch (error) {
    console.error("Delete upload failed", error);
    dispatch(setError("Failed to delete upload"));
  }
};

export const loadContainerUploads = () => ({
  type: FETCH_CONTAINER_UPLOADS,
});

export const putContainerUpload = () => ({
  type: PUT_CONTAINER_UPLOAD,
});

export const postContainerUpload = () => ({
  type: POST_CONTAINER_UPLOAD,
});

export const setContainerUploads = (containerUploads) => ({
  type: FETCH_CONTAINER_UPLOADS_SUCCESS,
  payload: {
    containerUploads: Array.isArray(containerUploads) ? containerUploads : [],
  },
});

export const loadNextContainerUploads = (page) => ({
  type: FETCH_NEXT_CONTAINER_UPLOADS,
  payload: page,
});

export const setNextContainerUploads = (containerUploads) => ({
  type: FETCH_NEXT_CONTAINER_UPLOADS_SUCCESS,
  payload: Array.isArray(containerUploads) ? containerUploads : [],
});

export const setError = (msg) => ({
  type: FETCH_CONTAINER_UPLOADS_ERROR,
  payload: msg,
});

/***********/
/* REDUCER */
/***********/
const containerUploadReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case FETCH_CONTAINER_UPLOADS:
      return {
        ...state,
        loading: true,
        error: "",
      };

    case FETCH_CONTAINER_UPLOADS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: "",
        data: payload?.containerUploads || [],
      };

    case PUT_CONTAINER_UPLOAD:
      return {
        ...state,
        loading: true,
        error: "",
      };

    case POST_CONTAINER_UPLOAD:
      return {
        ...state,
        loading: true,
        error: "",
      };

    case DELETE_CONTAINER_UPLOAD:
      return {
        ...state,
        loading: true,
        error: "",
      };

    case FETCH_CONTAINER_UPLOADS_ERROR:
      return {
        ...state,
        loading: false,
        error: payload,
      };

    case FETCH_NEXT_CONTAINER_UPLOADS:
      return {
        ...state,
        error: "",
      };

    case FETCH_NEXT_CONTAINER_UPLOADS_SUCCESS:
      return {
        ...state,
        loading: false,
        data: [...state.data, ...(Array.isArray(payload) ? payload : [])],
      };

    default:
      return state;
  }
};

export default containerUploadReducer;